import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { HashRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import api from "@/services/api";

// Pages
import LandingPage from "@/pages/LandingPage";
import WeddingForm from "@/pages/WeddingForm";
import ThankYou from "@/pages/ThankYou";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminSubmissions from "@/pages/AdminSubmissions";
import AdminSubmissionDetail from "@/pages/AdminSubmissionDetail";
import AdminUsers from "@/pages/AdminUsers";
import AdminSettings from "@/pages/AdminSettings";
import AdminFormOptions from "@/pages/AdminFormOptions";
import AdminProfile from "@/pages/AdminProfile";
import AdminLandingPage from "@/pages/AdminLandingPage";

// Auth Context
import { createContext, useContext } from "react";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.getMe(token);
      if (response.error) {
        localStorage.removeItem("token");
        setToken(null);
        setUser(null);
      } else {
        setUser(response);
      }
    } catch (error) {
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.login(email, password);
    if (response.error) {
      throw new Error(response.error);
    }
    const { access_token, user: userData } = response;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, token } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#c4646a]"></div>
      </div>
    );
  }

  if (!token || !user) {
    return <Navigate to="/admin" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

function AdminGate() {
  const { user, loading, token } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#c4646a]"></div>
      </div>
    );
  }

  if (token && user) {
    return <AdminDashboard />;
  }

  return <AdminLogin />;
}

function RedirectAdminSubmissionDetail() {
  const { id } = useParams();
  return <Navigate to={`/admin/submissions/${id}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <HashRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/form" element={<WeddingForm />} />
            <Route path="/f/form" element={<Navigate to="/form" replace />} />
            <Route path="/thank-you" element={<ThankYou />} />

            <Route path="/admin/login" element={<Navigate to="/admin" replace />} />
            <Route path="/admin" element={<AdminGate />} />
            <Route
              path="/admin/submissions"
              element={
                <ProtectedRoute>
                  <AdminSubmissions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/submissions/:id"
              element={
                <ProtectedRoute>
                  <AdminSubmissionDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/form-options"
              element={
                <ProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminFormOptions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/landing"
              element={
                <ProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminLandingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/profile"
              element={
                <ProtectedRoute>
                  <AdminProfile />
                </ProtectedRoute>
              }
            />

            <Route path="/manage" element={<Navigate to="/admin" replace />} />
            <Route path="/manage/*" element={<Navigate to="/admin" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </HashRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;
